import React from 'react';

import { Drawer, Input } from 'antd';
import PropTypes from 'prop-types';

import BoxContainer from 'components/BoxContainer';
import Title from 'components/BoxContainer/components/Title';
import FilterContainer from 'components/FilterContainer';
import SelectableCard from 'components/SelectableCard';
import StepIndicator from 'components/StepIndicator';

import IntlMessages from 'util/IntlMessages';

import {
  COMPONENTS_OF_METRICS_OPTIONS,
  DASHBOARDS_FORMS,
} from '../../screens/EventsDashboard/constants';
import {
  CountChartForm,
  CounterForm,
  CounterNumberComponent,
  DeviationsFromTaskCompliance,
  EventsForm,
  PieChartForm,
  ScheduleComplianceForm,
  ScheduleComplianceHistForm,
  TableForm,
} from '../../screens/EventsDashboard/forms';
import { useMetricsDrawer } from './hooks';
import styles from './styles.module.less';

const MetricsDrawer = ({
  open,
  loading,
  metricsComponenteSelected,
  someChartTypeSelected,
  boardSelected,
  componentConfig,
  onClose,
  setMetricsComponenteSelected,
}) => {
  const {
    search,
    isEditing,
    onBackAction,
    buttonItems,
    handleSearchChange,
    currentElementSelected,
    registerFormRef,
  } = useMetricsDrawer({
    metricsComponenteSelected,
    componentConfig,
    onClose,
    setMetricsComponenteSelected,
  });

  const METRICS_COMPONENTS = {
    [DASHBOARDS_FORMS.STATISTIC_ONLY_NUMBER_COMPONENTS]: (
      <EventsForm
        chartId={currentElementSelected[DASHBOARDS_FORMS.STATISTIC_ONLY_NUMBER_COMPONENTS]?.id}
        divId={boardSelected?.divId}
        boardId={boardSelected?.id}
        ref={registerFormRef(DASHBOARDS_FORMS.STATISTIC_ONLY_NUMBER_COMPONENTS)}
      />
    ),
    [DASHBOARDS_FORMS.EVENT_TABLE]: (
      <TableForm
        tableId={currentElementSelected[DASHBOARDS_FORMS.EVENT_TABLE]}
        divId={boardSelected?.divId}
        boardId={boardSelected?.id}
        ref={registerFormRef(DASHBOARDS_FORMS.EVENT_TABLE)}
      />
    ),
    [DASHBOARDS_FORMS.EVENT_COUNTER]: (
      <PieChartForm
        chartId={currentElementSelected[DASHBOARDS_FORMS.EVENT_COUNTER]?.id}
        divId={boardSelected?.divId}
        boardId={boardSelected?.id}
        ref={registerFormRef(DASHBOARDS_FORMS.EVENT_COUNTER)}
      />
    ),
    [DASHBOARDS_FORMS.EVENT_COUNTER_BY_TIME]: (
      <CountChartForm
        chartId={currentElementSelected[DASHBOARDS_FORMS.EVENT_COUNTER_BY_TIME]?.id}
        divId={boardSelected?.divId}
        boardId={boardSelected?.id}
        ref={registerFormRef(DASHBOARDS_FORMS.EVENT_COUNTER_BY_TIME)}
      />
    ),
    [DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_SUMMARY]: (
      <CounterForm
        chartId={currentElementSelected[DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_SUMMARY]?.id}
        divId={boardSelected?.divId}
        boardId={boardSelected?.id}
        ref={registerFormRef(DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_SUMMARY)}
      />
    ),
    [DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE]: (
      <ScheduleComplianceForm
        chartId={currentElementSelected[DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE]?.id}
        divId={boardSelected?.divId}
        boardId={boardSelected?.id}
        ref={registerFormRef(DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE)}
      />
    ),
    [DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_HIST]: (
      <ScheduleComplianceHistForm
        chartId={currentElementSelected[DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_HIST]?.id}
        divId={boardSelected?.divId}
        boardId={boardSelected?.id}
        ref={registerFormRef(DASHBOARDS_FORMS.SCHEDULE_COMPLIANCE_HIST)}
      />
    ),
    [DASHBOARDS_FORMS.DEVIATIONS_FROM_TASK_COMPLIANCE]: (
      <DeviationsFromTaskCompliance
        chartId={currentElementSelected[DASHBOARDS_FORMS.DEVIATIONS_FROM_TASK_COMPLIANCE]?.id}
        divId={boardSelected?.divId}
        boardId={boardSelected?.id}
        ref={registerFormRef(DASHBOARDS_FORMS.DEVIATIONS_FROM_TASK_COMPLIANCE)}
      />
    ),
    [DASHBOARDS_FORMS.EVENT_COUNTER_NUMBER_COMPONENT]: (
      <CounterNumberComponent
        chartId={currentElementSelected[DASHBOARDS_FORMS.EVENT_COUNTER_NUMBER_COMPONENT]?.id}
        divId={boardSelected?.divId}
        boardId={boardSelected?.id}
        ref={registerFormRef(DASHBOARDS_FORMS.EVENT_COUNTER_NUMBER_COMPONENT)}
      />
    ),
  };

  return (
    <Drawer open={open} onClose={onClose} closable={false} footer={null}>
      <BoxContainer>
        <BoxContainer content loading={loading}>
          <FilterContainer
            goBack={onBackAction}
            title={
              <Title.Header
                value={
                  someChartTypeSelected ? (
                    <IntlMessages id="dashboards.component.edit" />
                  ) : (
                    <IntlMessages id="dashboards.component.new" />
                  )
                }
              />
            }
            buttonItems={buttonItems}
          />
        </BoxContainer>
        <BoxContainer content className={styles.contentContainer}>
          {!metricsComponenteSelected ? (
            <>
              {!isEditing && (
                <>
                  <StepIndicator
                    currentStep={1}
                    totalSteps={2}
                    stepTitles={[
                      'dashboards.component.step1.title',
                      'dashboards.component.step2.title',
                    ]}
                  />
                  <div className="gx-pt-4">
                    <Input.Search
                      value={search}
                      placeholder="Search"
                      onChange={handleSearchChange}
                    />
                  </div>
                </>
              )}
              {someChartTypeSelected ? null : (
                <div className={styles.scrollableContainer}>
                  {COMPONENTS_OF_METRICS_OPTIONS.filter(item =>
                    item.label.toLowerCase().includes(search.toLowerCase()),
                  ).map(item => (
                    <SelectableCard
                      key={item.value}
                      icon={item.icon}
                      title={item.label}
                      description={item.description}
                      selected={metricsComponenteSelected === item.value}
                      onClick={() => setMetricsComponenteSelected(item.value)}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {!isEditing && (
                <StepIndicator
                  currentStep={2}
                  totalSteps={2}
                  stepTitles={[
                    'dashboards.component.step1.title',
                    'dashboards.component.step2.title',
                  ]}
                  stepCounterId="dashboards.component.step"
                />
              )}
              <div className="gx-pb-4 gx-pl-2">
                <Title.SubTitle value={<IntlMessages id="dashboard.component.configure" />} />
              </div>
              {METRICS_COMPONENTS[metricsComponenteSelected]}
            </>
          )}
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};

MetricsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  metricsComponenteSelected: PropTypes.string,
  someChartTypeSelected: PropTypes.bool.isRequired,
  boardSelected: PropTypes.shape({
    id: PropTypes.string,
    divId: PropTypes.string,
  }).isRequired,
  componentConfig: PropTypes.shape({
    formRefsMap: PropTypes.shape({
      current: PropTypes.object,
    }).isRequired,
    currentElementSelectedRef: PropTypes.shape({
      current: PropTypes.object,
    }).isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  setMetricsComponenteSelected: PropTypes.func.isRequired,
};

export default MetricsDrawer;
